import React, { useState, useEffect } from 'react'
import LogoPreview from './LogoPreview'
import LogoOptions from './LogoOptions'
import LogoFlagPreview from './components/LogoFlagPreview'
import { presets } from './util/presets'

// Define the logo state interface
interface LogoState {
  backgroundColor: string
  icon: string
  iconPack: string
  iconColor: string
  exactIconSize: number
  borderRadius: number
  borderWidth: number
  iconBorderWidth: number
  padding: number
  rotate: number
  id?: string
  savedAt?: string
}

const LogoEditor = () => {
  const [logo, setLogo] = useState<LogoState>({
    backgroundColor: '#ffffff',
    icon: 'faStar', // Default to fa-star
    iconPack: 'font-awesome-solid',
    iconColor: '#000000',
    exactIconSize: 64,
    borderRadius: 8,
    borderWidth: 0,
    iconBorderWidth: 0,
    padding: 16,
    rotate: 0,
  })

  const [hasSelectedIcon, setHasSelectedIcon] = useState(false)
  const [savedLogos, setSavedLogos] = useState<LogoState[]>([])

  // Load saved logos from localStorage on component mount
  useEffect(() => {
    console.log('Loading saved logos from localStorage...')
    const saved = localStorage.getItem('logobest-saved-logos')
    console.log('Raw localStorage data:', saved)
    if (saved) {
      try {
        const parsedLogos = JSON.parse(saved)
        console.log('Parsed logos:', parsedLogos)
        setSavedLogos(parsedLogos)
      } catch (error) {
        console.error('Failed to parse saved logos:', error)
        localStorage.removeItem('logobest-saved-logos')
      }
    } else {
      console.log('No saved logos found in localStorage')
    }
  }, [])

  // Save logos to localStorage whenever savedLogos changes
  useEffect(() => {
    console.log('Saving logos to localStorage:', savedLogos)
    if (savedLogos.length > 0) {
      localStorage.setItem('logobest-saved-logos', JSON.stringify(savedLogos))
      console.log('Logos saved to localStorage successfully')
    } else {
      localStorage.removeItem('logobest-saved-logos')
      console.log('Removed empty localStorage entry')
    }
  }, [savedLogos])

  const handleLogoChange = (updatedLogo: Partial<LogoState>) => {
    if (updatedLogo.icon && !hasSelectedIcon) {
      setHasSelectedIcon(true)
    }
    setLogo({ ...logo, ...updatedLogo })
  }

  const handlePresetClick = (preset: Partial<LogoState>) => {
    const updatedLogo = {
      ...logo,
      ...preset,
    }
    if (hasSelectedIcon) {
      updatedLogo.icon = logo.icon
      updatedLogo.iconPack = logo.iconPack
    }
    setLogo(updatedLogo)
  }

  const handleSaveLogo = () => {
    const logoToSave: LogoState = {
      ...logo,
      id: Date.now().toString(),
      savedAt: new Date().toISOString(),
    }

    console.log('Saving logo:', logoToSave)
    setSavedLogos((prev) => [logoToSave, ...prev])
  }

  const handleLoadLogo = (savedLogo: LogoState) => {
    console.log('Loading saved logo:', savedLogo)
    setLogo(savedLogo)
    setHasSelectedIcon(true)
  }

  const handleDeleteLogo = (logoId: string) => {
    console.log('Deleting logo with ID:', logoId)
    setSavedLogos((prev) => prev.filter((logo) => logo.id !== logoId))
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col lg:flex-row w-full max-w-5xl gap-4 md:gap-6 mx-auto">
        {/* Preview Section - Sticky on desktop */}
        <div className="w-full lg:w-2/3 order-1">
          <div className="lg:sticky lg:top-24">
            <LogoPreview
              logo={logo}
              onSave={handleSaveLogo}
              savedLogos={savedLogos}
            />
            {/* Preset Row */}
            <div className="mt-6">
              <div className="flex flex-row gap-4 overflow-x-auto pb-4 px-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                {presets.map((preset, idx) => (
                  <div
                    key={idx}
                    style={{ opacity: 0.7, cursor: 'pointer' }}
                    onClick={() => handlePresetClick(preset)}
                  >
                    <LogoFlagPreview
                      backgroundColor={preset.backgroundColor}
                      iconColor={preset.iconColor}
                      exactIconSize={preset.exactIconSize}
                      borderRadius={preset.borderRadius}
                      borderWidth={preset.borderWidth}
                      rotate={preset.rotate}
                      ratio={0.3}
                      icon={hasSelectedIcon ? logo.icon : preset.icon}
                      iconPack={
                        hasSelectedIcon
                          ? logo.iconPack
                          : preset.iconPack || 'font-awesome-solid'
                      }
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* History Section - Same styling as templates */}
            {savedLogos.length > 0 && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-3 px-2">Saved Logos</h3>
                <div className="flex flex-row gap-4 overflow-x-auto pb-4 px-2 pt-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                  {savedLogos.map((savedLogo) => (
                    <div key={savedLogo.id} className="relative group">
                      <div
                        className="cursor-pointer transform transition-transform hover:scale-105"
                        onClick={() => handleLoadLogo(savedLogo)}
                      >
                        <LogoFlagPreview
                          backgroundColor={savedLogo.backgroundColor}
                          iconColor={savedLogo.iconColor}
                          exactIconSize={savedLogo.exactIconSize}
                          borderRadius={savedLogo.borderRadius}
                          borderWidth={savedLogo.borderWidth}
                          rotate={savedLogo.rotate}
                          ratio={0.3}
                          icon={savedLogo.icon}
                          iconPack={savedLogo.iconPack}
                        />
                      </div>

                      {/* Delete button - appears on hover */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteLogo(savedLogo.id!)
                        }}
                        className="absolute -top-1 -right-1 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-lg z-10"
                        title="Delete saved logo"
                      >
                        <svg
                          className="w-3 h-3"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Options Section */}
        <div className="w-full lg:w-1/3 order-2">
          <LogoOptions logo={logo} onChange={handleLogoChange} />
        </div>
      </div>
    </div>
  )
}

export default LogoEditor
